import { Box, Button, Card, IconButton, Typography } from '@mui/material';
import ScrollForwardIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ScrollBackIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LoadingIndicator,
  handleHorizontalScroll,
  handleHorizontalClicked,
  useWindow,
  FunctionBuilder,
} from '@xolv-mybrightlink/common';
import { EventCard } from './EventCard';
import { FormattedMessage } from 'react-intl';
import { useFetchPreviewEvents } from '../hooks/useFetchEvents';
import { CmsEvent } from '../models/EventResult';

export const EventListPreview = () => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [events, setEvents] = useState<CmsEvent[]>([]);
  const [retry, setRetry] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const eventFetchResult = useFetchPreviewEvents(undefined, null, 10, retry);

  const checkHorizontalScrollable = useCallback(() => {
    if (containerRef.current && events.length > 0) {
      setCanScrollRight(
        containerRef.current.scrollWidth - containerRef.current.clientWidth > 0,
      );
    }
  }, [containerRef, events]);

  useWindow(() => {
    checkHorizontalScrollable();
  });

  useEffect(() => {
    checkHorizontalScrollable();
  }, [checkHorizontalScrollable]);

  useEffect(() => {
    if (eventFetchResult.type === 'DATA') {
      setEvents(eventFetchResult.events);
    }
  }, [eventFetchResult]);

  const onScroll = () => {
    handleHorizontalScroll(containerRef, (canScrollLeft, canScrollRight) => {
      setCanScrollLeft(canScrollLeft);
      setCanScrollRight(canScrollRight);
    });
  };

  const onScrollIconClicked = (direction: 'backward' | 'forward') => {
    handleHorizontalClicked(
      containerRef,
      direction,
      (canScrollLeft, canScrollRight) => {
        setCanScrollLeft(canScrollLeft);
        setCanScrollRight(canScrollRight);
      },
    );
  };

  return (
    <Box
      height={320}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <FunctionBuilder>
          {() => {
            if (eventFetchResult.type === 'ERROR') {
              return (
                <Card
                  elevation={8}
                  sx={{
                    width: '100%',
                    height: 300,
                    borderRadius: 5,
                    m: 2,
                    padding: 0,
                    background: 'transparent',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    <FormattedMessage
                      id='common.error.fetch.fail'
                      values={{ br: <br /> }}
                    />
                  </Typography>
                  <Button
                    variant={'contained'}
                    disableElevation
                    onClick={() => setRetry(!retry)}
                    sx={{
                      width: '20%',
                      height: '18%',
                      borderRadius: 8,
                      mt: 10,
                      fontSize: 'inherit',
                    }}
                  >
                    <FormattedMessage id='common.try-again' />
                  </Button>
                </Card>
              );
            } else if (eventFetchResult.type === 'LOADING') {
              return <LoadingIndicator />;
            } else {
              return (
                <>
                  <Box
                    className='hide-scrollbar'
                    ref={containerRef}
                    onScroll={onScroll}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      overflow: 'auto',
                      mr: 5,
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    {events.map((item) => (
                      <EventCard key={item.databaseId} cmsEvent={item} />
                    ))}
                  </Box>
                  {canScrollLeft && (
                    <IconButton
                      onClick={() => onScrollIconClicked('backward')}
                      sx={{
                        borderRadius: 5,
                        backgroundColor: '#ffffff',
                        position: 'absolute',
                        width: 40,
                        height: 40,
                        left: -12,
                        boxShadow: 4,
                        p: 5,
                        '&:hover': {
                          backgroundColor: 'white',
                        },
                      }}
                    >
                      <ScrollBackIcon
                        sx={{
                          color: '#898f9b',
                        }}
                      />
                    </IconButton>
                  )}
                  {canScrollRight && (
                    <IconButton
                      onClick={() => onScrollIconClicked('forward')}
                      sx={{
                        borderRadius: 20,
                        backgroundColor: '#ffffff',
                        position: 'absolute',
                        width: 40,
                        height: 40,
                        right: 4,
                        boxShadow: 4,
                        p: 5,
                        '&:hover': {
                          backgroundColor: 'white',
                        },
                      }}
                    >
                      <ScrollForwardIcon
                        sx={{
                          color: '#898f9b',
                        }}
                      />
                    </IconButton>
                  )}
                </>
              );
            }
          }}
        </FunctionBuilder>
      </Box>
    </Box>
  );
};
